import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Lightweight, app-wide usage snapshot. Components subscribe via this hook.
 *  Call `refresh()` after any action that may have consumed a limit.
 */
let _cache = null;
let _subscribers = new Set();

function _notify() {
    for (const fn of _subscribers) fn(_cache);
}

export async function refreshUsage() {
    try {
        const r = await api.get("/usage");
        _cache = r.data;
    } catch {
        _cache = { active: false, reason: "fetch_error" };
    }
    _notify();
    return _cache;
}

export function useUsage() {
    const [usage, setUsage] = useState(_cache);

    useEffect(() => {
        _subscribers.add(setUsage);
        if (_cache === null) refreshUsage();
        return () => { _subscribers.delete(setUsage); };
    }, []);

    const refresh = useCallback(() => refreshUsage(), []);

    /** Returns { used, limit, remaining, isExhausted, isWarning } for a given key
     *  Returns null if usage not loaded or limit not consumable.
     */
    const getLimit = useCallback((key) => {
        if (!usage?.limits) return null;
        const canonical = ({
            regenerations: "regenerations_per_month",
            csv_analyses: "csv_analyses_per_month",
        })[key] || key;
        const row = usage.limits.find((l) => l.key === canonical);
        if (!row) return null;
        const remaining = row.remaining ?? Math.max(0, (row.limit || 0) - (row.used || 0));
        return {
            ...row,
            remaining,
            isExhausted: row.limit > 0 && remaining <= 0,
            isWarning: row.limit > 0 && (row.used / row.limit) >= 0.8,
        };
    }, [usage]);

    return { usage, refresh, getLimit };
}
